// tuner/index.ts — the version switcher. Both panel versions operate on the SAME live `feel` object
// and the same bus; only the control-panel UI differs. The dropdown lets Jason walk v1.0 → v1.1 to
// show how the tuning tool itself evolved. Add v1.2 by dropping a module and one registry entry.

import { setSubmit } from "./fire";
import { mountV1_0 } from "./v1_0";
import { mountV1_1 } from "./v1_1";
import { mountV1_2 } from "./v1_2";
import { APP_VERSION, BUILD_NOTES } from "../version";
import { slamUp } from "../ui/reveal";

// The two under-the-hood rails, revealed by the intro on the player's second engagement.
let railsRefs: { left: HTMLElement; right: HTMLElement } | null = null;
const RAIL_STAGGER = 1200; // left rail slams up, then the right one ~0.7s after it lands (one at a time)
const HINT_AFTER_SLAM = 700; // a rail's tutorial prompt draws in ~0.7s after that rail lands
export function revealRails(hooks?: { onLeft?: () => void; onRight?: () => void }): void {
  if (!railsRefs) return;
  slamUp(railsRefs.left, 0);
  slamUp(railsRefs.right, RAIL_STAGGER);
  if (hooks?.onLeft) window.setTimeout(hooks.onLeft, HINT_AFTER_SLAM);
  if (hooks?.onRight) window.setTimeout(hooks.onRight, RAIL_STAGGER + HINT_AFTER_SLAM);
}

/** The rail shells (for the tutorial arm to hang its prompts off). Null until mountTuner runs. */
export function getRails(): { left: HTMLElement; right: HTMLElement } | null {
  return railsRefs;
}

// A/B experiment: A (default) = plain app · B = the handwritten tutorial.
// Resolution order: an explicit ?ab=A|B in the URL wins (shareable links — ephemeral, never written
// to storage, so a link can't infect a visitor's saved default); else the saved pill preference; else A.
const AB_KEY = "dn_ab";
const AB_PARAM = "ab";
export function tutorialEnabled(): boolean {
  const p = new URLSearchParams(location.search).get(AB_PARAM)?.toUpperCase();
  if (p === "B") return true;
  if (p === "A") return false;
  return localStorage.getItem(AB_KEY) === "B";
}

interface Version {
  id: string; // major.minor line, used for ?panel= routing
  name: string; // descriptive suffix
  // Mount into the RIGHT rail (Juice); v1.2 also uses the LEFT rail (Personality). Returns a disposer.
  mount: (rightHost: HTMLElement, leftHost: HTMLElement) => () => void;
}

// Newest last; the default is the last entry (or ?panel=<id>).
const VERSIONS: Version[] = [
  { id: "1.0", name: "Tweakpane", mount: mountV1_0 },
  { id: "1.1", name: "Double/Halve + auto-fire", mount: mountV1_1 },
  { id: "1.2", name: "+ Personality", mount: mountV1_2 },
];

// The dropdown label tracks the live build version: the entry on the current minor line shows the
// full APP_VERSION (e.g. "v1.2.5 · + Personality"), so a patch bump updates the selector too.
const APP_MINOR = APP_VERSION.split(".").slice(0, 2).join(".");
function versionLabel(v: Version): string {
  return `v${v.id === APP_MINOR ? APP_VERSION : v.id} · ${v.name}`;
}

export function mountTuner(submit: (word: string) => void): void {
  setSubmit(submit); // wire the real submit path into the auto-fire engine

  // LEFT rail — Personality (flanks the game board on the left).
  const leftShell = document.createElement("div");
  leftShell.className = "tuner-shell tuner-left";
  const leftHost = document.createElement("div");
  leftHost.className = "tuner-panel-host";
  leftShell.append(leftHost);

  // RIGHT rail — Juice (flanks the board on the right).
  const shell = document.createElement("div");
  shell.className = "tuner-shell tuner-right";
  const panelHost = document.createElement("div");
  panelHost.className = "tuner-panel-host";

  const select = document.createElement("select");
  select.className = "tuner-version";
  for (const v of VERSIONS) {
    const opt = document.createElement("option");
    opt.value = v.id;
    opt.textContent = versionLabel(v);
    select.appendChild(opt);
  }

  const caption = document.createElement("button");
  caption.className = "tuner-caption";
  caption.textContent = `v${APP_VERSION} ⌄`;
  caption.title = "build notes — click to view what changed per version";

  // Build-notes panel, hidden until the version is clicked.
  const notes = document.createElement("div");
  notes.className = "build-notes";
  notes.hidden = true;
  notes.innerHTML =
    `<div class="build-notes-head">Build notes</div>` +
    BUILD_NOTES.map(
      (n) => `<div class="build-note"><b>v${n.v}</b><span>${n.note}</span></div>`,
    ).join("");
  caption.addEventListener("click", () => {
    notes.hidden = !notes.hidden;
  });

  // A|B experiment pill — A (plain) · B (tutorial). Manual opt-in; the choice is saved and the page
  // reloads so the tutorial runs (or stops) from a clean boot.
  const ab = document.createElement("div");
  ab.className = "ab-toggle";
  ab.setAttribute(
    "data-tip",
    "A = plain app · B = guided tutorial. Sets the arm in the URL (?ab=A/?ab=B) — copy the address bar to share a link that launches that arm. Reloads on switch.",
  );
  ab.innerHTML = `<span class="ab-label">tut</span><button class="ab-opt" data-v="A" type="button">A</button><button class="ab-opt" data-v="B" type="button">B</button>`;
  const current = tutorialEnabled() ? "B" : "A";
  ab.querySelector(`.ab-opt[data-v="${current}"]`)?.classList.add("on");
  ab.querySelectorAll<HTMLButtonElement>(".ab-opt").forEach((b) =>
    b.addEventListener("click", () => {
      const v = b.dataset.v!;
      if (v === current) return;
      localStorage.setItem(AB_KEY, v); // sticky pill preference (used when there's no ?ab= in the URL)
      const url = new URL(location.href);
      url.searchParams.set(AB_PARAM, v); // reflect the arm in the address bar → that's the shareable link
      location.href = url.toString(); // navigate (reload) into the chosen arm
    }),
  );

  // Version switcher lives in the game window's top bar (right-justified), not in a rail.
  const slot = document.querySelector<HTMLElement>(".version-slot");
  slot?.append(caption, select, notes, ab);

  shell.append(panelHost);
  // Rails are invisible but KEPT in the layout (reserve their width) so the centre never jerks when
  // they appear — the intro just makes them visible + slams them up on the first mash.
  leftShell.classList.add("pre-reveal-reserve");
  shell.classList.add("pre-reveal-reserve");
  railsRefs = { left: leftShell, right: shell };
  document.body.append(leftShell, shell);

  let dispose: (() => void) | null = null;
  function show(id: string): void {
    dispose?.();
    panelHost.innerHTML = "";
    leftHost.innerHTML = "";
    const v = VERSIONS.find((x) => x.id === id) ?? VERSIONS[VERSIONS.length - 1];
    select.value = v.id;
    dispose = v.mount(panelHost, leftHost);
    // The left rail only exists for versions that fill it (v1.2 = Personality); hide it otherwise.
    leftShell.style.display = leftHost.childElementCount ? "" : "none";
    const url = new URL(location.href);
    url.searchParams.set("panel", v.id);
    history.replaceState(null, "", url);
  }

  select.addEventListener("change", () => show(select.value));

  const requested = new URLSearchParams(location.search).get("panel");
  show(requested && VERSIONS.some((v) => v.id === requested) ? requested : VERSIONS[VERSIONS.length - 1].id);
}
